import { z } from 'zod';
import { AppLocale } from './locale.js';
/**
 * Step 2 — identity verification (PLAN.md §15).
 *
 * Didit's own vocabulary is wider than anything the funnel needs to branch on,
 * and it is a vendor's vocabulary rather than ours. The UI gets this reduced
 * set instead, so swapping providers later is a mapping change rather than a
 * rewrite of every screen that renders a status.
 */
export const KYC_STATES = [
    /** No session created yet, or the last one was thrown away. */
    'not_started',
    /** The user is inside Didit's hosted flow, or has been asked to resubmit. */
    'in_progress',
    /** Automated checks did not decide it. A human at Didit or compliance will. */
    'in_review',
    'approved',
    'declined',
    /** The session ran out of time or the user walked away. A new one is needed. */
    'expired',
];
export const KycState = z.enum(KYC_STATES);
/**
 * Didit's raw `status` → our state.
 *
 * Kept as an explicit table rather than a lowercase/underscore transform: an
 * unrecognised status must land somewhere deliberate, and silently coining a
 * new state from a vendor string is how a funnel ends up with a screen nobody
 * has designed.
 */
const FROM_DIDIT = {
    'Not Started': 'not_started',
    'In Progress': 'in_progress',
    'Awaiting User': 'in_progress',
    Resubmitted: 'in_progress',
    'In Review': 'in_review',
    Approved: 'approved',
    Declined: 'declined',
    Expired: 'expired',
    Abandoned: 'expired',
    'Kyc Expired': 'expired',
};
/**
 * Maps a Didit status onto our state, defaulting to `in_progress`.
 *
 * The default is deliberate and is the safe direction: an unknown status must
 * never read as `approved` (that would let an unverified user through) nor as
 * `declined` (that would reject a real customer on a vendor's typo). Leaving
 * them mid-flight means the webhook or the next poll decides.
 */
export function fromDiditStatus(status) {
    if (!status)
        return 'not_started';
    return FROM_DIDIT[status] ?? 'in_progress';
}
/** Terminal for the funnel: nothing the user does will move it. */
export function isTerminalKycState(state) {
    return state === 'approved' || state === 'declined';
}
/**
 * The documents are in: approved, or with a human review still pending.
 *
 * This is no longer a gate — the funnel lets every applicant reach the end
 * whatever Didit said (§15.4) — but it is still the difference between an
 * identity we hold and one we have yet to collect, so it decides what the CRM
 * is told and what the applicant is told is outstanding.
 */
export function isKycSubmitted(state) {
    return state === 'approved' || state === 'in_review';
}
/**
 * A residential address line. Long enough for a real one, capped because it
 * ends up rendered in a CRM screen a human reads.
 */
const AddressLine = z
    .string()
    .trim()
    .min(4, 'Enter your street address.')
    .max(160, 'Use 160 characters or fewer.');
const AddressPart = z
    .string()
    .trim()
    .min(1, 'This is required.')
    .max(64, 'Use 64 characters or fewer.');
/**
 * Posted to start verification. It carries no body any more.
 *
 * The residential address used to be collected here, before the handoff, on the
 * theory that this was the last screen we owned. It has moved to a confirmation
 * screen *after* Didit returns (`ProfileSubmitRequest`), where Didit's own OCR
 * prefills it and the applicant only corrects it — a lazier, higher-completing
 * ask than typing an address from scratch before the camera flow. Kept as a
 * schema rather than removed so the route keeps a single validation point and a
 * future pre-handoff field is a one-line change.
 */
export const KycSessionRequest = z.object({
    /**
     * The locale the user is reading *now*, which is not necessarily the one they
     * submitted step 1 in. Didit fixes the hosted flow's language at session
     * creation, so the live value has to travel with this request rather than be
     * read back off the stored session.
     */
    locale: AppLocale.optional(),
});
/**
 * What the client gets when it asks to start (or resume) verification.
 *
 * `url` is Didit's hosted session. We deliberately do NOT return the
 * `session_token` — it authenticates the session to Didit's SDK, and the
 * browser only needs somewhere to go.
 */
export const KycSessionResponse = z.object({
    state: KycState,
    /** Absent once the session reached a state that has no hosted page left. */
    url: z.string().url().optional(),
    /** Didit's session id, echoed so the client can correlate its polling. */
    sessionId: z.string(),
});
export const KycStatusResponse = z.object({
    state: KycState,
    sessionId: z.string().optional(),
    /**
     * Set when `declined` and Didit gave a reason we are willing to show. Free
     * text from the vendor is never passed through — this is a code the UI owns
     * copy for.
     */
    reason: z.enum(['document_unreadable', 'face_mismatch', 'document_expired', 'other']).optional(),
    /** The funnel step the client should now be on. */
    step: z.number().int().min(1).max(4),
});
/**
 * A person's name, permissive on script (Thai, Japanese and Chinese names are
 * the majority of this funnel) and strict only on characters that have no
 * business in a name. The same rule step 1 used to apply — the post-KYC form is
 * where the name is actually confirmed now.
 */
const PersonName = z
    .string()
    .trim()
    .min(1, 'Enter your name.')
    .max(64, 'Use 64 characters or fewer.')
    // Latin letters only, as they appear on the document's machine-readable zone:
    // the CRM and the compliance record are kept in A-Z, so a Vietnamese name is
    // filed without its diacritics and a Thai one transliterated. Space, hyphen
    // and apostrophe are the punctuation a real name carries.
    .regex(/^[A-Za-z][A-Za-z '-]*$/, 'Use letters A-Z only.');
/**
 * What Didit's OCR read, offered to prefill the confirmation screen (§11).
 *
 * Every field is optional: a passport carries no address, a sandbox scenario
 * returns all-null, and a field Didit could not read must leave the input blank
 * rather than block the screen. This is a starting point the applicant owns and
 * can overwrite — never an assertion.
 */
export const ProfilePrefill = z.object({
    firstName: z.string().optional(),
    lastName: z.string().optional(),
    dateOfBirth: z.string().optional(),
    address: z.string().optional(),
    city: z.string().optional(),
    state: z.string().optional(),
});
export const ProfilePrefillResponse = z.object({
    /** So the screen can gate on the identity actually having been submitted. */
    state: KycState,
    prefill: ProfilePrefill,
});
/**
 * The confirmed profile, posted after Didit returns. These are the values the
 * client's name and the CRM document's `city` / `state` / `address` fields are
 * filed from — the applicant's own claim, which is why it wins over OCR in
 * `resolveAddress`.
 */
export const ProfileSubmitRequest = z.object({
    firstName: PersonName,
    lastName: PersonName,
    /** ISO `YYYY-MM-DD` from a native date input; the server reformats for the CRM. */
    dateOfBirth: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Enter your date of birth.'),
    address: AddressLine,
    city: AddressPart,
    /** Named for the CRM's own field. "State / Province / Region" to the user. */
    state: AddressPart,
});
export const ProfileSubmitResponse = z.object({
    /** The step the session advanced to. The client never chooses this. */
    step: z.number().int().min(1).max(4),
});
//# sourceMappingURL=kyc.js.map